import { PdfRedactionAnnotation } from '@syncfusion/ej2-pdf';
import { _GraphicState } from '../graphic-state';
import { _PdfContentParserHelper } from '../content-parser-helper';
import { _TextProcessingMode } from '../enum';
import { _addFontResources, _getXObjectResources } from '../utils';
import { _TextGlyphMapper } from './text-glyph-mapper';
import { _PdfRedactionProcessor } from './pdf-redaction-processor';
import { PdfRedactionRegion } from './pdf-redaction-region';
/**
 * Represents a content redactor from an existing PDF document.
 *
 * ```typescript
 * let document: PdfDocument = new PdfDocument(data, password);
 * // Create a new text extractor
 * let redactor: PdfRedactor = new PdfRedactor(document);
 * // Add redactions to the collection
 * let redactions: PdfRedactionRegion[] = [];
 * redactions.push(new PdfRedactionRegion(0, {x: 10, y: 10, width: 100, height: 50}));
 * redactions.push(new PdfRedactionRegion(2, {x: 10, y: 10, width: 100, height: 50}, true, [255, 0, 0]));
 * redactor.add(redactions);
 * // Apply redactions on the PDF document
 * redactor.redact();
 * // Save the document
 * document.save('output.pdf');
 * // Destroy the document
 * document.destroy();
 * ```
 */
var PdfRedactor = /** @class */ (function () {
    function PdfRedactor(document, redactions) {
        this._isHex = false;
        this._redactionRegion = [];
        this._redaction = new Map();
        this._object = new _PdfRedactionProcessor();
        if (document) {
            if (document._isLoaded) {
                this._document = document;
                this._crossReference = document._crossReference;
            }
            else {
                throw new Error('Redaction cannot be applied to a newly created document.');
            }
            this._document.fileStructure.isIncrementalUpdate = false;
            this._parser = new _PdfContentParserHelper(_TextProcessingMode.redaction, this);
            if (redactions && redactions.length > 0) {
                this.add(redactions);
            }
        }
        else {
            throw new Error('PDF document instance cannot be null or undefined');
        }
    }
    /**
     * Add redactions with specified options.
     *
     * @param {PdfRedactionRegion[]} redactions An array of redaction objects to specify the page index, bounds and appearance of the redaction to be applied.
     * @returns {void} Nothing.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Add redactions to the collection
     * let redactions: PdfRedactionRegion[] = [];
     * // Initialize a new instance of the `PdfRedactor` class
     * let redactor: PdfRedactor = new PdfRedactor(document);
     * // Initialize a new instance of the `PdfRedactionRegion` class.
     * let redaction: PdfRedactionRegion = new PdfRedactionRegion(0, {x:40, y: 41.809, width: 80, height: 90});
     * // Sets the fill color used to fill the redacted area.
     * redaction.fillColor = [255, 0, 0];
     * redactions.push(redaction);
     * // Add redactions with specified options.
     * redactor.add(redactions);
     * // Apply redactions on the PDF document
     * redactor.redact();
     * // Save the document
     * document.save('output.pdf');
     * // Destroy the document
     * document.destroy();
     * ```
     */
    PdfRedactor.prototype.add = function (redactions) {
        var options = redactions;
        for (var i = 0; i < options.length; i++) {
            var pageIndex = options[Number.parseInt(i.toString(), 10)].pageIndex;
            if (!this._redaction.has(pageIndex)) {
                this._redaction.set(pageIndex, []);
            }
            var redactionArray = this._redaction.get(pageIndex);
            if (redactionArray) {
                redactionArray.push(options[Number.parseInt(i.toString(), 10)]);
            }
        }
    };
    /**
     * Redact the PDF document
     *
     * @returns {void} Nothing.
     *
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data, password);
     * // Add redactions to the collection
     * let redactions: PdfRedactionRegion[] = [];
     * // Initialize a new instance of the `PdfRedactor` class
     * let redactor: PdfRedactor = new PdfRedactor(document);
     * // Initialize a new instance of the `PdfRedactionRegion` class.
     * let redaction: PdfRedactionRegion = new PdfRedactionRegion(0, {x:40, y: 41.809, width: 80, height: 90});
     * // Sets the fill color used to fill the redacted area.
     * redaction.fillColor = [255, 0, 0];
     * redactions.push(redaction);
     * // Add redactions with specified options.
     * redactor.add(redactions);
     * // Apply redactions on the PDF document
     * redactor.redact();
     * // Save the document
     * document.save('output.pdf');
     * // Destroy the document
     * document.destroy();
     * ```
     */
    PdfRedactor.prototype.redact = function () {
        var _this = this;
        if (this._document && this._document.pageCount > 0) {
            for (var i = 0; i < this._document.pageCount; i++) {
                var page = this._document.getPage(i);
                if (page && page.annotations.count > 0) {
                    this._applyRedaction(page);
                }
            }
        }
        this._redaction.forEach(function (value, key) {
            _this._redactionRegion = [];
            _this._combineBounds(value);
            var option = value;
            var page = _this._document.getPage(key);
            var graphicState = new _GraphicState();
            var recordCollection = _this._parser._getPageRecordCollection(page);
            var resource = page._pageDictionary.get('Resources');
            var fontCollection;
            var xObjectCollection;
            if (typeof (resource) !== 'undefined') {
                fontCollection = _addFontResources(resource, page._pageDictionary._crossReference);
                xObjectCollection = _getXObjectResources(resource, page._pageDictionary._crossReference);
            }
            var stream; // eslint-disable-line
            if (_this._redactionRegion.length > 0) {
                stream = _this._parser._processRecordCollection(recordCollection, page, fontCollection, xObjectCollection, graphicState);
            }
            page._needInitializeGraphics = true;
            _this._object._updateContentStream(page, stream, option, _this._document);
        });
    };
    PdfRedactor.prototype._applyRedaction = function (page) {
        var redactRegions = [];
        var _loop_1 = function (k) {
            var annotation = page.annotations.at(k);
            if (annotation && annotation instanceof PdfRedactionAnnotation) {
                var redactionAnnotation_1 = annotation;
                redactionAnnotation_1.flatten = true;
                if (redactionAnnotation_1.boundsCollection && redactionAnnotation_1.boundsCollection.length > 1) {
                    redactionAnnotation_1.boundsCollection.forEach(function (value, index) {
                        var redact = new PdfRedactionRegion(page._pageIndex, { x: value[0], y: value[1],
                            width: value[2], height: value[3] });
                        redact.appearance.normal = redactionAnnotation_1._createNormalAppearance(index);
                        redactRegions.push(redact);
                    });
                }
                else {
                    var appearance = redactionAnnotation_1._createNormalAppearance();
                    var redact = new PdfRedactionRegion(page._pageIndex, redactionAnnotation_1.bounds);
                    redact.appearance.normal = appearance;
                    redactRegions.push(redact);
                }
                page.annotations.removeAt(k);
                k--;
            }
            out_k_1 = k;
        };
        var out_k_1;
        for (var k = 0; k < page.annotations.count; k++) {
            _loop_1(k);
            k = out_k_1;
        }
        if (redactRegions.length > 0) {
            var existingRedactions = this._redaction.get(page._pageIndex) || [];
            this._redaction.set(page._pageIndex, existingRedactions.concat(redactRegions));
        }
    };
    PdfRedactor.prototype._combineBounds = function (options) {
        for (var i = 0; i < options.length; i++) {
            this._redactionRegion.push(options[Number.parseInt(i.toString(), 10)]);
        }
    };
    PdfRedactor.prototype._optimizeContent = function (recordCollection, index, updatedText, stream) {
        var record = recordCollection[Number.parseInt(index.toString(), 10)];
        if (record) {
            if (typeof (record._operands) !== 'undefined' && record._operands.length >= 1) {
                if (record._operator === 'ID') {
                    var builder = [];
                    for (var k = 0; k < record._operands.length; k++) {
                        if (k + 1 < record._operands.length && record._operands[k].indexOf("/") !== -1 && record._operands[k + 1].indexOf("/") !== -1) { // eslint-disable-line
                            builder.push(record._operands[Number.parseInt(k.toString(), 10)], ' ', record._operands[k + 1], '\r\n');
                            k = k + 1;
                        }
                        else if (k + 1 < record._operands.length && record._operands[k].indexOf("/") !== -1) { // eslint-disable-line
                            builder.push(record._operands[Number.parseInt(k.toString(), 10)], ' ', record._operands[k + 1], '\r\n');
                            k = k + 1;
                        }
                        else {
                            builder.push(record._operands[Number.parseInt(k.toString(), 10)], ' ');
                        }
                    }
                    var text = builder.join(""); // eslint-disable-line
                    var bytes = this._getBytes(text);
                    stream.write(bytes);
                }
                else {
                    for (var i = 0; i < record._operands.length; i++) {
                        var operand = record._operands[Number.parseInt(i.toString(), 10)];
                        if (record._operator === 'Tj' || record._operator === "'" || record._operator === '\"' || record._operator === 'TJ') { // eslint-disable-line
                            if (updatedText !== '') {
                                operand = updatedText;
                                if (record._operator === "'" || record._operator === '\"') { // eslint-disable-line
                                    stream.write('T*');
                                    stream.write(' ');
                                    if (record._operator === '\"') { // eslint-disable-line
                                        i += 2;
                                    }
                                }
                                record._operator = 'TJ';
                            }
                        }
                        var bytes = this._getBytes(operand);
                        stream.write(bytes);
                        if (record._operator !== 'Tj' && record._operator !== "'" && record._operator !== '\"' && record._operator !== 'TJ') { // eslint-disable-line
                            stream.write(' ');
                        }
                    }
                }
            }
            else if (typeof (record._operands) === 'undefined' && typeof (record._inlineImageBytes) !== 'undefined') {
                var numberArray = Array.from(record._inlineImageBytes);
                stream.write(numberArray);
                stream.write(' ');
            }
            stream.write(record._operator);
        }
        var count = recordCollection.length;
        if ((index) < count) {
            if (record._operator === 'ID') {
                stream.write('\n');
            }
            else if ((record._operator === 'W' || record._operator === 'W*') && recordCollection[index + 1]._operator === 'n') {
                stream.write(' ');
            }
            else if (record._operator === 'w' || record._operator === 'EI') {
                stream.write(' ');
            }
            else {
                stream.write('\r\n');
            }
        }
    };
    PdfRedactor.prototype._getBytes = function (text) {
        var bytes = [];
        for (var i = 0; i < text.length; i++) {
            var charCode = text.charCodeAt(i);
            bytes.push(charCode);
        }
        return bytes;
    };
    PdfRedactor.prototype._isFoundBounds = function (values, redactionBounds) {
        var rect = { x: values[0], y: values[1], width: values[2],
            height: values[3] };
        for (var _i = 0, redactionBounds_1 = redactionBounds; _i < redactionBounds_1.length; _i++) {
            var bounds = redactionBounds_1[_i];
            if (this._contains(bounds._bounds, [rect.x, rect.y]) || this._intersectsWith(bounds._bounds, rect)) {
                return true;
            }
        }
        return false;
    };
    PdfRedactor.prototype._contains = function (bounds, point) {
        return (point[0] >= bounds.x &&
            point[0] <= bounds.x + bounds.width &&
            point[1] >= bounds.y &&
            point[1] <= bounds.y + bounds.height);
    };
    PdfRedactor.prototype._intersectsWith = function (rect1, rect2) {
        return (rect2.x < rect1.x + rect1.width) && (rect1.x < (rect2.x + rect2.width)) && (rect2.y < rect1.y + rect1.height) &&
            (rect1.y < rect2.y + rect2.height);
    };
    PdfRedactor.prototype._splitHexString = function (hexString) {
        var hexList = [];
        hexString = hexString.slice(1, -1);
        var size = hexString.startsWith('0') ? 4 : 2;
        for (var i = 0; i < hexString.length; i += size) {
            var chunk = hexString.substring(i, i + size);
            if (chunk.indexOf('\n') !== -1) {
                var extraChar = hexString.charAt(i + size);
                chunk += extraChar;
                i++;
            }
            hexList.push(chunk);
        }
        return hexList;
    };
    PdfRedactor.prototype._replacedText = function (glyph, text, originalText, decodeText) {
        var isReplacedText = false;
        var isOtherText = false;
        for (var i = 0; i < glyph.length; i++) {
            if (this._isFoundBounds(glyph[Number.parseInt(i.toString(), 10)]._bounds, this._redactionRegion)) {
                isReplacedText = true;
                glyph[Number.parseInt(i.toString(), 10)]._isReplace = true;
            }
            else {
                isOtherText = true;
                glyph[Number.parseInt(i.toString(), 10)]._text = text[Number.parseInt(i.toString(), 10)];
            }
        }
        var updatedText = '';
        if (!isReplacedText && isOtherText) {
            return originalText;
        }
        else {
            var mainTextCollection = [];
            if (originalText[0] === '(') {
                mainTextCollection = decodeText;
            }
            else if (originalText[0] === '[') {
                mainTextCollection = decodeText;
            }
            else if (originalText[0] === '<') {
                mainTextCollection = decodeText;
                this._isHex = true;
            }
            var map = this._mapString(mainTextCollection, glyph);
            for (var i = 0; i < map.length; i++) {
                map[Number.parseInt(i.toString(), 10)]._isHex = this._isHex;
                updatedText += map[Number.parseInt(i.toString(), 10)]._getText();
            }
            updatedText = '[' + updatedText + ']';
            this._isHex = false;
        }
        return updatedText;
    };
    PdfRedactor.prototype._mapString = function (mainTextCollection, imageGlyph) {
        var mappedString = [];
        var glyphList = imageGlyph;
        var startIndex = 0;
        for (var i = 0; i < mainTextCollection.length; i++) {
            var endChar = mainTextCollection[Number.parseInt(i.toString(), 10)].length - 1;
            if (mainTextCollection[Number.parseInt(i.toString(), 10)][0] !== '(' && mainTextCollection[Number.parseInt(i.toString(), 10)][Number.parseInt(endChar.toString(), 10)] !== ')') {
                var mapping = new _TextGlyphMapper();
                mapping.text = mainTextCollection[Number.parseInt(i.toString(), 10)];
                mappedString.push(mapping);
            }
            else {
                var mapping = new _TextGlyphMapper();
                mapping.text = mainTextCollection[Number.parseInt(i.toString(), 10)];
                var text = mainTextCollection[Number.parseInt(i.toString(), 10)];
                var subString = text.length >= 2;
                var start = text.startsWith('(');
                var end = text.endsWith(')');
                if (subString && start && !end) {
                    text = text.substring(1, text.length);
                }
                else if (subString && !start && end) {
                    text = text.substring(0, text.length - 1);
                }
                else if (subString) {
                    text = text.substring(1, text.length - 1);
                }
                else {
                    continue;
                }
                var length_1 = text.length;
                mapping.glyph = glyphList.slice(startIndex, startIndex + length_1);
                startIndex += length_1;
                mappedString.push(mapping);
            }
        }
        return mappedString;
    };
    return PdfRedactor;
}());
export { PdfRedactor };
